import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Eye, EyeOff, GripVertical } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';

const ManageFAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ question: '', answer: '', order: 0, is_published: true });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.getAllFAQ();
      setFaqs(res.data.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch { toast.error('Failed to load FAQs'); }
  };

  const resetForm = () => {
    setForm({ question: '', answer: '', order: faqs.length, is_published: true });
    setEditing(null);
  };

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }
    try {
      if (editing) {
        await api.updateFAQ(editing, form);
        toast.success('FAQ updated');
      } else {
        await api.createFAQ({ ...form, order: faqs.length });
        toast.success('FAQ created');
      }
      resetForm();
      load();
    } catch { toast.error('Failed to save FAQ'); }
  };

  const handleEdit = (f) => {
    setEditing(f.id);
    setForm({ question: f.question, answer: f.answer, order: f.order || 0, is_published: f.is_published });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await api.deleteFAQ(id);
      toast.success('FAQ deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const togglePublish = async (f) => {
    try {
      await api.updateFAQ(f.id, { is_published: !f.is_published });
      load();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div data-testid="manage-faq">
      <h2 className="font-serif text-2xl text-[#3E2723] mb-6">Manage FAQ</h2>

      {/* Form */}
      <div className="border border-[#E6DCD3] rounded-xl p-6 mb-8 bg-[#FCFBF9]" data-testid="faq-form">
        <h3 className="text-sm font-medium text-[#1A1A1A] mb-4">{editing ? 'Edit FAQ' : 'Add FAQ'}</h3>
        <input
          value={form.question}
          onChange={e => setForm({...form, question: e.target.value})}
          placeholder="Question"
          className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961] mb-4"
          data-testid="faq-question-input"
        />
        <textarea
          value={form.answer}
          onChange={e => setForm({...form, answer: e.target.value})}
          placeholder="Answer"
          rows={3}
          className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961] mb-4"
          data-testid="faq-answer-input"
        />
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_published} onChange={e => setForm({...form, is_published: e.target.checked})} className="rounded" />
            Published
          </label>
          <div className="flex-1" />
          {editing && <button onClick={resetForm} className="text-sm text-[#786E66] hover:text-[#1A1A1A]">Cancel</button>}
          <button onClick={handleSave} className="bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full px-6 py-2 text-sm transition-all inline-flex items-center gap-2" data-testid="faq-save-btn">
            <Plus className="h-4 w-4" /> {editing ? 'Update' : 'Add'}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-3" data-testid="faq-list">
        {faqs.map((f, idx) => (
          <div key={f.id} className={`border rounded-xl p-5 ${f.is_published ? 'border-[#E6DCD3] bg-white' : 'border-gray-200 bg-gray-50 opacity-70'}`} data-testid={`faq-item-${idx}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <GripVertical className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  <p className="font-medium text-[#1A1A1A] text-sm">{f.question}</p>
                </div>
                <p className="text-sm text-[#786E66] ml-6 line-clamp-2">{f.answer}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => togglePublish(f)} className="p-2 hover:bg-[#F3EBE1] rounded-lg" title={f.is_published ? 'Unpublish' : 'Publish'}>
                  {f.is_published ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
                </button>
                <button onClick={() => handleEdit(f)} className="p-2 hover:bg-[#F3EBE1] rounded-lg"><Pencil className="h-4 w-4 text-[#3E2723]" /></button>
                <button onClick={() => handleDelete(f.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="h-4 w-4 text-red-500" /></button>
              </div>
            </div>
          </div>
        ))}
        {faqs.length === 0 && <p className="text-center text-[#786E66] text-sm py-8">No FAQs yet. Add one above.</p>}
      </div>
    </div>
  );
};

export default ManageFAQ;
